/*
  File: navigation.ts
  Path: src/config/navigation.ts
  Description: Phase C navigation config (grouped) driven by ui_mode + permissions from /api/v1/me.
               IMPORTANT: permissions are the only gating input. tier_level is display-only.

  Author: Larry McLean + AI Team
  Created: 2026-01-08
  Version: 1.0.3

  Last Updated: 2026-07-28
  Status: Active (Phase C)

  Change Log (append-only):
    - 2026-01-08 - v1.0.0 (Larry McLean + AI Team)
      - Create Phase C nav config driven by ui_mode + permissions
      - Use only canonical permission keys present in /api/v1/me today
      - Support grouped nav + hide/disabled display modes
    - 2026-07-28 - v1.0.1 - Add the Manifest sidebar entry.
    - 2026-08-11 - v1.0.2 - Distinct iconKey per PRO item 
      - (was reusing overview/calendar/bookings/activity). [Larry McLean / Claude]
    - 2026-08-11 - v1.0.3 - menu items 
      - Reorganized the order of the left side navigation menu items.
*/

export type UiMode = 'admin' | 'pro' | 'affiliate';

export type DisplayMode = 'show' | 'hide' | 'disabled';

export type PermissionKey =
  | 'can_use_admin_tools'
  | 'can_use_operator_tools'
  | 'can_use_affiliate_tools'
  | 'can_view_schedule'
  | 'can_edit_profile'
  | 'can_manage_operator'
  | 'can_edit'
  | 'can_approve'
  | 'can_modify_config';

export type Permissions = Partial<Record<PermissionKey, boolean>>;

export interface NavItem {
  label: string;
  path: string;

  // Which shell(s) this item belongs to
  uiModes: UiMode[];

  // Permission gating: AND semantics (all must be true)
  requiredPermissions?: PermissionKey[];

  // Default behavior when unauthorized: hide (Phase C default)
  display?: DisplayMode;

  // For disabled items only
  disabledReason?: string;

  // Keep icons simple for now; we can map iconKey -> Lucide later
  iconKey?: string;
}

export interface NavGroup {
  group: string;
  uiModes: UiMode[];
  items: NavItem[];
}

export const NAV_CONFIG: NavGroup[] = [
  // -----------------------------
  // ADMIN
  // -----------------------------
  {
    group: 'Platform',
    uiModes: ['admin'],
    items: [
      {
        label: 'Overview',
        path: '/admin/overview',
        uiModes: ['admin'],
        iconKey: 'overview',
      },
      {
        label: 'Users',
        path: '/admin/users',
        uiModes: ['admin'],
        iconKey: 'users',
        requiredPermissions: ['can_use_admin_tools'],
      },
      {
        label: 'System Health',
        path: '/admin/system-health',
        uiModes: ['admin'],
        iconKey: 'health',
        requiredPermissions: ['can_modify_config'],
      },
      {
        label: 'Audit Log',
        path: '/admin/audit',
        uiModes: ['admin'],
        iconKey: 'audit',
        display: 'disabled',
        disabledReason: 'Coming soon',
      },
    ],
  },

  // -----------------------------
  // PRO
  // -----------------------------
  {
    group: 'Operations',
    uiModes: ['pro'],
    items: [
      {
        label: 'Ops Board',
        path: '/dashboard',
        uiModes: ['pro'],
        iconKey: 'opsboard',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Calendar',
        path: '/dashboard/calendar',
        uiModes: ['pro'],
        iconKey: 'calendar',
        requiredPermissions: ['can_view_schedule'],
      },
      {
        label: 'Schedule',
        path: '/dashboard/schedule',
        uiModes: ['pro'],
        iconKey: 'schedule',
        requiredPermissions: ['can_view_schedule'],
      },
      {
        label: 'Bookings',
        path: '/dashboard/bookings',
        uiModes: ['pro'],
        iconKey: 'bookings',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Book Now',
        path: '/dashboard/walk-ins',
        uiModes: ['pro'],
        iconKey: 'walkins',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Billing',
        path: '/dashboard/billing',
        uiModes: ['pro'],
        iconKey: 'billing',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Agencies',
        path: '/dashboard/agencies',
        uiModes: ['pro'],
        iconKey: 'Agencies',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Courses',
        path: '/dashboard/courses',
        uiModes: ['pro'],
        iconKey: 'courses',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Forms',
        path: '/dashboard/forms',
        uiModes: ['pro'],
        iconKey: 'Forms',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Dive Sites',
        path: '/dashboard/sites',
        uiModes: ['pro'],
        iconKey: 'sites',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Vehicles & Vessels',
        path: '/dashboard/vehicles-vessels',
        uiModes: ['pro'],
        iconKey: 'vessels',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Customers',
        path: '/dashboard/customers',
        uiModes: ['pro'],
        iconKey: 'customers',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Team',
        path: '/dashboard/team',
        uiModes: ['pro'],
        iconKey: 'team',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Content & Media',
        path: '/dashboard/content-media',
        uiModes: ['pro'],
        iconKey: 'content-media',
        requiredPermissions: ['can_use_operator_tools'],
      },
      {
        label: 'Vitals',
        path: '/dashboard/vitals',
        uiModes: ['pro'],
        iconKey: 'vitals',
        requiredPermissions: ['can_use_operator_tools'],
      },
    ],
  },
  {
    group: 'Account',
    uiModes: ['pro'],
    items: [
      {
        label: 'Settings',
        path: '/dashboard/settings',
        uiModes: ['pro'],
        iconKey: 'settings',
        requiredPermissions: ['can_use_operator_tools'],
      },
    ],
  },

  // -----------------------------
  // AFFILIATE
  // -----------------------------
  {
    group: 'Partner',
    uiModes: ['affiliate'],
    items: [
      {
        label: 'Overview',
        path: '/partner/overview',
        uiModes: ['affiliate'],
        iconKey: 'overview',
        requiredPermissions: ['can_use_affiliate_tools'],
      },
      {
        label: 'Commissions',
        path: '/partner/commissions',
        uiModes: ['affiliate'],
        iconKey: 'commissions',
        display: 'disabled',
        disabledReason: 'Coming soon',
      },
    ],
  },
];

/**
 * Returns only nav groups relevant to a ui_mode.
 */
export function getNavForMode(uiMode: UiMode): NavGroup[] {
  return NAV_CONFIG.filter((g) => g.uiModes.includes(uiMode));
}

/**
 * Filter items by permissions.
 * Phase C policy:
 * - Default unauthorized behavior: HIDE
 * - If display === 'disabled', keep item but mark disabled
 */
export function resolveVisibleNav(
  uiMode: UiMode,
  permissions: Permissions
): Array<NavGroup & { items: Array<NavItem & { isDisabled?: boolean; reason?: string }> }> {
  const groups = getNavForMode(uiMode);

  return groups
    .map((group) => {
      const resolvedItems = group.items
        .map((item) => {
          const required = item.requiredPermissions ?? [];
          const allowed = required.every((k) => Boolean(permissions?.[k]));

          if (allowed) return { ...item, isDisabled: false };

          const display = item.display ?? 'hide';

          if (display === 'disabled') {
            return {
              ...item,
              isDisabled: true,
              reason: item.disabledReason || 'Unavailable',
            };
          }

          // hide by default
          return null;
        })
        .filter(Boolean) as Array<NavItem & { isDisabled?: boolean; reason?: string }>;

      return { ...group, items: resolvedItems };
    })
    .filter((g) => g.items.length > 0);
}